const FALSY_STRINGS = new Set(['0', 'false', 'no', 'n', 'non', 'none', '']);

export const LINE_GEOMETRY_TYPES = ['LineString', 'MultiLineString'];
export const POINT_GEOMETRY_TYPES = ['Point', 'MultiPoint'];

export function hasGeometryOfType(geojson: any, types: string[]): boolean {
  const features = geojson?.features;
  if (!Array.isArray(features)) return false;
  return features.some((f: any) => types.includes(f?.geometry?.type));
}

export interface FeatureWidthConfig {
  mode: 'manual' | 'field';
  /** Width in metres (manual mode). */
  width?: number;
  /** Property name whose (metre) values become each feature's width (field mode). */
  field?: string;
}

/**
 * Stamp a `width` property (metres) onto every feature whose geometry type is
 * in `geometryTypes`, so the backend can buffer lines/points into polygons.
 * In field mode, non-numeric field values are skipped (the backend then falls
 * back to its default width).
 */
export function applyWidthConfig(
  geojson: any,
  config: FeatureWidthConfig,
  geometryTypes: string[]
): any {
  if (!geojson || !Array.isArray(geojson.features)) return geojson;
  return {
    ...geojson,
    features: geojson.features.map((feature: any) => {
      if (!geometryTypes.includes(feature?.geometry?.type)) return feature;
      const width =
        config.mode === 'manual'
          ? config.width
          : config.field
          ? Number(feature?.properties?.[config.field])
          : undefined;
      if (typeof width !== 'number' || !Number.isFinite(width) || width <= 0) {
        return feature;
      }
      return { ...feature, properties: { ...feature.properties, width } };
    }),
  };
}

export function collectPropertyFields(geojson: any): string[] {
  if (!geojson) return [];
  const collections = Array.isArray(geojson) ? geojson : [geojson];
  const keys = new Set<string>();
  for (const collection of collections) {
    for (const feature of collection?.features ?? []) {
      if (feature?.properties && typeof feature.properties === 'object') {
        Object.keys(feature.properties).forEach((k) => keys.add(k));
      }
    }
  }
  return Array.from(keys).sort((a, b) => a.localeCompare(b));
}

export function collectFieldSamples(geojson: any, field: string, limit = 3): string[] {
  if (!geojson || !field) return [];
  const collections = Array.isArray(geojson) ? geojson : [geojson];
  const samples: string[] = [];
  const seen = new Set<string>();
  for (const collection of collections) {
    for (const feature of collection?.features ?? []) {
      const value = feature?.properties?.[field];
      if (value === undefined || value === null) continue;
      const str = String(value);
      if (!seen.has(str)) {
        seen.add(str);
        samples.push(str);
        if (samples.length >= limit) return samples;
      }
    }
  }
  return samples;
}

export function isFlammableFieldValue(value: unknown): boolean {
  if (value === null || value === undefined) return false;
  if (typeof value === 'boolean') return value;
  if (typeof value === 'number') return value !== 0;
  return !FALSY_STRINGS.has(String(value).toLowerCase().trim());
}

/**
 * Parse a list-valued asset property that may arrive either as a real array
 * (from the assets GeoJSON endpoint) or as a JSON-encoded string (from vector
 * tiles, which can only store scalar property values). Always returns a
 * number[] — used for `extra_construction_setting_ids`.
 */
export function parseIdList(value: unknown): number[] {
  if (Array.isArray(value)) return value.map((v) => Number(v)).filter((n) => !Number.isNaN(n));
  if (typeof value === 'string' && value.trim()) {
    try {
      const parsed = JSON.parse(value);
      if (Array.isArray(parsed)) return parsed.map((v) => Number(v)).filter((n) => !Number.isNaN(n));
    } catch {
      /* not JSON — ignore */
    }
  }
  return [];
}

/**
 * Parse a string-list asset property (array or JSON-encoded string) into a
 * string[]. Used for `extra_construction_setting_names`.
 */
export function parseStringList(value: unknown): string[] {
  if (Array.isArray(value)) return value.map((v) => String(v));
  if (typeof value === 'string' && value.trim()) {
    try {
      const parsed = JSON.parse(value);
      if (Array.isArray(parsed)) return parsed.map((v) => String(v));
    } catch {
      /* not JSON — ignore */
    }
  }
  return [];
}
